import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from '../../../ui/toast';
import { Button } from '../../../ui/components/Common/Buttons/Button';
import { CustomSelect } from '../../../ui/custom_react_select/custom_select';
import { DatePicker } from '../../../ui/input/date_picker/date_picker';
import { TextInput } from '../../../ui/input/text_input/text_input';
import { ToggleInput } from '../../../ui/input/toggle_input/toggle_input';
import { getOrganizationSubscriptions, updateLicense } from '../../../utils/api_service/admin';
import { createOrganization, getOrganizationById } from '../../../utils/api_service/organization';
import { LicenseDateRange, OrganizationCreate, OrganizationLicenses } from '../../../utils/types/admin_types';

const CreateOrganization = () => {
  const history = useHistory();
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>(
    'monthly',
  );

  const [organizationLicenses, setOrganizationLicenses] = useState<OrganizationLicenses>({
    organizationId: '',
    hierTalentBase: undefined,
    hierTalentAutoAssessments: undefined,
    hierTalentAutoClassify: undefined,
    hierTalentJobPost: undefined,
    hierTalentSmartInterviews: undefined,
    hierTalentCandiBank: undefined,
    hierTalentCca: undefined,
    hierTalentPsychometricFaxtor: undefined,
    hierTalentWellness: undefined,
    hierTalentReporting: undefined,
  });

  const [formData, setFormData] = useState<OrganizationCreate>({
    organization_name: '',
    organization_domains: [],
    first_name: '',
    last_name: '',
    email: '',
    interval: 'month',
    company_size: 'Small',
  });

  const companySizeOptions: { label: string; value: OrganizationCreate['company_size'] }[] = [
    { label: 'Small', value: 'Small' },
    { label: 'Medium', value: 'Medium' },
    { label: 'Large', value: 'Large' },
    { label: 'Large Enterprise', value: 'Large Enterprise' },
  ];

  const handleChange = (field: keyof OrganizationCreate, value: OrganizationCreate[keyof OrganizationCreate]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const LICENSE_KEY_MAP: Record<string, keyof Omit<OrganizationLicenses, 'organizationId'>> = {
    hierTalentBase: 'hierTalentBase',
    autoAssessments: 'hierTalentAutoAssessments',
    autoClassify: 'hierTalentAutoClassify',
    jobPost: 'hierTalentJobPost',
    smartInterviews: 'hierTalentSmartInterviews',
    candiBank: 'hierTalentCandiBank',
    cca: 'hierTalentCca',
    psychometricFaxtor: 'hierTalentPsychometricFaxtor',
    wellness: 'hierTalentWellness',
    reporting: 'hierTalentReporting',
  };

  const toggleLicense = (uiId: string) => {
    const key = LICENSE_KEY_MAP[uiId];
    setOrganizationLicenses((prev) => ({
      ...prev,
      // If currently active (has dates), set to undefined. If inactive, set empty strings as placeholder.
      [key]: prev[key] ? undefined : { startDate: '', endDate: '' },
    }));
  };

  const handleCreateOrganization = async () => {
    try {
      const res = await createOrganization(formData);
      if (res.ok) {
        const response = await res.json();
        const organizationId = response.organization.id;

        toast.success('Organization created.', { position: 'bottom-center' });

        const updatedLicenses = { ...organizationLicenses, organizationId };
        const licenseRes = await updateLicense(updatedLicenses);

        if (licenseRes.ok) {
          toast.success('License updated successfully.', { position: 'bottom-center' });
        }
      }
    } catch (e) {
      toast.error('Error creating organization or updating license.', {
        position: 'bottom-center',
      });
    }
  };

  const isFormIncomplete =
      !formData.organization_name.trim() ||
      formData.organization_domains.length === 0 ||
      !formData.first_name.trim() ||
      !formData.last_name.trim() ||
      !formData.email.trim();

  const isCreateDisabled = isFormIncomplete || Object.values(LICENSE_KEY_MAP).some((key) => {
    const license = organizationLicenses[key];
    return license !== undefined && (!license.startDate || !license.endDate);
  });

  const disabledTooltip = isFormIncomplete
    ? 'Please fill in all organization and administrator details.'
    : 'Fill in start & end dates for all active licenses.';


  /** Metered licenses carry a monthly allowance. Blank = unlimited (null), 0 = blocked. */
  const monthlyCreditsValue = (uiId: string): string => {
    const value = organizationLicenses[LICENSE_KEY_MAP[uiId]]?.monthlyCredits;
    return value === null || value === undefined ? '' : String(value);
  };

  const updateMonthlyCredits = (uiId: string, raw: string) => {
    const key = LICENSE_KEY_MAP[uiId];
    const trimmed = raw.trim();
    const parsed = trimmed === '' ? null : Math.max(0, Math.floor(Number(trimmed)));
    setOrganizationLicenses((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? { startDate: '', endDate: '' }), monthlyCredits: Number.isNaN(parsed) ? null : parsed },
    }));
  };

  const updateLicenseDate = (uiId: string, field: keyof LicenseDateRange, value: any) => {
    const key = LICENSE_KEY_MAP[uiId];
    // DatePicker returns an object like { startDate: "2026-04-01", endDate: "2026-04-01" }
    // Extract just the startDate string from it
    const dateString = typeof value === 'object' && value !== null ? value.startDate : value;

    setOrganizationLicenses((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: dateString },
    }));
  };

  return (
    <div className='p-8 bg-slate-50/50 min-h-screen font-sans'>
      <div className='max-w-[1200px] mx-auto'>
        { /* Breadcrumbs */ }
        <nav className='flex items-center gap-2 mb-6 text-sm font-medium'>
          <span
              className='text-slate-400 cursor-pointer hover:text-slate-600 transition-colors'
              onClick={() => history.push('/organizations')}
          >
            Organizations
          </span>
          <svg className='w-3 h-3 text-slate-300' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={3} d='M9 5l7 7-7 7' />
          </svg>
          <span className='text-emerald-600'>Create New</span>
        </nav>

        { /* Header */ }
        <div className='mb-10'>
          <h1 className='text-4xl font-black text-slate-800 tracking-tighter mb-2'>
            Create Organization
          </h1>
          <p className='text-slate-500 text-sm font-medium'>
            Register a new client entity and set up their administrative account.
          </p>
        </div>

        { /* Main Content Card */ }
        <div className='bg-white rounded-[32px] border border-slate-100 shadow-xl shadow-slate-200/40 overflow-hidden'>
          <div className='p-10 space-y-12'>
            { /* Identity Section */ }
            <div>
              <h2 className='text-[11px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-8'>
                Identity
              </h2>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
                <TextInput
                    label='Organization Name'
                    name='name'
                    value={formData.organization_name}
                    type='text'
                    placeholder='e.g. Acme Corporation'
                    onChange={(e) => handleChange('organization_name', e.target.value)}
                />
                <CustomSelect
                    label='Company Size'
                    name='size'
                    options={companySizeOptions}
                    placeholder='Select size...'
                    onChange={(option: any) => handleChange('company_size', option.value)}
                />
                <TextInput
                    label='Approved Domains'
                    name='domains'
                    value={formData.organization_domains.join(', ')}
                    type='text'
                    placeholder='acme.com, acme.io (comma separated)'
                    onChange={(e) => handleChange('organization_domains', e.target.value.split(',').map(d => d.trim()).filter(Boolean))}
                />
              </div>
              <p className='mt-3 text-[10px] text-slate-400 font-medium ml-1'>
                User emails must match these domains to join automatically.
              </p>
            </div>

            { /* Primary Administrator Section */ }
            <div>
              <h2 className='text-[11px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-8'>
                Primary Administrator
              </h2>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
                <TextInput
                    label='First Name'
                    name='firstName'
                    value={formData.first_name}
                    type='text'
                    placeholder='John'
                    onChange={(e) => handleChange('first_name', e.target.value)}
                />
                <TextInput
                    label='Last Name'
                    name='lastName'
                    value={formData.last_name}
                    type='text'
                    placeholder='Doe'
                    onChange={(e) => handleChange('last_name', e.target.value)}
                />
                <TextInput
                    label='Administrator Email'
                    name='email'
                    value={formData.email}
                    type='email'
                    placeholder='john.doe@acme.com'
                    onChange={(e) => handleChange('email', e.target.value)}
                />
              </div>
            </div>

            { /* Subscription Plan Section */ }
            <div>
              <h2 className='text-[11px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-8'>
                Subscription Plan
              </h2>
              <div>
                <label
                    className='block mb-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider ml-0.5'>
                  Billing Interval
                </label>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  { /* Monthly Card */ }
                  <div
                      onClick={() => {
                        setBillingInterval('monthly');
                        handleChange('interval', 'month');
                      }}
                      className={`relative p-6 rounded-2xl border-2 transition-all cursor-pointer group ${
                        billingInterval === 'monthly'
                          ? 'border-emerald-500 bg-emerald-50/30'
                          : 'border-slate-100 bg-slate-50/30 hover:border-slate-200'
                      }`}
                  >
                    <div className='flex flex-col items-center justify-center text-center py-4'>
                      <span
                          className={`text-lg font-black mb-1 ${billingInterval === 'monthly' ? 'text-slate-800' : 'text-slate-600'}`}>Monthly</span>
                      <span className='text-xs font-medium text-slate-400'>Flexible payments</span>
                    </div>
                    { billingInterval === 'monthly' && (
                      <div className='absolute top-4 right-4 text-emerald-500'>
                        <svg className='w-5 h-5' fill='currentColor' viewBox='0 0 20 20'>
                          <path fillRule='evenodd'
                              d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                              clipRule='evenodd'/>
                        </svg>
                      </div>
                    ) }
                  </div>

                  { /* Yearly Card */ }
                  <div
                      onClick={() => {
                        setBillingInterval('yearly');
                        handleChange('interval', 'year');
                      }}
                      className={`relative p-6 rounded-2xl border-2 transition-all cursor-pointer overflow-hidden group ${
                        billingInterval === 'yearly'
                          ? 'border-emerald-500 bg-emerald-50/30'
                          : 'border-slate-100 bg-slate-50/30 hover:border-slate-200'
                      }`}
                  >
                    { /* Ribbon */ }
                    <div
                        className='absolute -right-12 top-4 w-40 bg-emerald-500 text-white text-[10px] font-bold py-1 rotate-45 text-center shadow-lg'>
                      SAVE 15%
                    </div>
                    <div className='flex flex-col items-center justify-center text-center py-4'>
                      <span
                          className={`text-lg font-black mb-1 ${billingInterval === 'yearly' ? 'text-slate-800' : 'text-slate-600'}`}>Yearly</span>
                      <span className='text-xs font-medium text-slate-400'>Best value</span>
                    </div>
                    { billingInterval === 'yearly' && (
                      <div className='absolute top-4 left-4 text-emerald-500'>
                        <svg className='w-5 h-5' fill='currentColor' viewBox='0 0 20 20'>
                          <path fillRule='evenodd'
                              d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                              clipRule='evenodd'/>
                        </svg>
                      </div>
                    ) }
                  </div>
                </div>
              </div>
            </div>
            <div className='bg-white rounded-[2rem] border border-slate-100 shadow-sm p-8'>
              <div className='flex items-center gap-2 mb-8'>
                <svg className='w-5 h-5 text-emerald-500' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2.5}
                      d='M9 12l2 2 4-4m5.618-4.016A3.323 3.323 0 0010.605 2.02a2.337 2.337 0 00-4.28 1.103 4.484 4.484 0 01.633 1.89h.001a1 1 0 001 1h8.423a1 1 0 011 1v8.423a1 1 0 001 1h.001a4.484 4.484 0 011.89.633 2.337 2.337 0 001.103-4.28 3.323 3.323 0 00-4.016-5.618z'/>
                </svg>
                <h2 className='text-lg font-black text-slate-800 tracking-tight text-[16px]'>License
                  Configuration</h2>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
                { [
                  {
                    id: 'hierTalentBase',
                    label: 'HierTalent Base',
                    icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
                    color: 'bg-emerald-50 text-emerald-600',
                  },
                  {
                    id: 'autoAssessments',
                    label: 'Auto Assessments',
                    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
                    color: 'bg-blue-50 text-blue-600',
                  },
                  {
                    id: 'autoClassify',
                    label: 'Auto Classify',
                    icon: 'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z',
                    color: 'bg-indigo-50 text-indigo-600',
                  },
                  {
                    id: 'jobPost',
                    label: 'Job Post',
                    icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
                    color: 'bg-amber-50 text-amber-600',
                  },
                  {
                    id: 'smartInterviews',
                    label: 'Smart Interviews',
                    icon: 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z',
                    color: 'bg-rose-50 text-rose-600',
                  },
                  {
                    id: 'candiBank',
                    label: 'CandiBank',
                    icon: 'M8 14v20M24 14v20M8 24h16',
                    color: 'bg-slate-900 text-white',
                    isSpecialIcon: true,
                    creditUnit: 'CVs',
                  },
                  {
                    id: 'psychometricFaxtor',
                    label: 'Psychometric Faxtor',
                    icon: 'M9.5 2a4.5 4.5 0 00-4.5 4.5v.5a4 4 0 00-2 3.5 4 4 0 002 3.5v1a4.5 4.5 0 004.5 4.5h.5M14.5 2a4.5 4.5 0 014.5 4.5v.5a4 4 0 012 3.5 4 4 0 01-2 3.5v1a4.5 4.5 0 01-4.5 4.5h-.5M9.5 2v18M14.5 2v18',
                    color: 'bg-violet-50 text-violet-600',
                  },
                  {
                    id: 'wellness',
                    label: 'Wellness',
                    icon: 'M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 10-7.78 7.78l1.06 1.06L12 21l7.78-7.55 1.06-1.06a5.5 5.5 0 000-7.78z',
                    color: 'bg-teal-50 text-teal-600',
                  },
                ].map((item) => {
                  const isActive = !!organizationLicenses[LICENSE_KEY_MAP[item.id]];
                  return (
                    <div
                        key={item.id}
                        className={`p-6 rounded-3xl border shadow-sm transition-all group relative ${
                          isActive
                            ? 'bg-white border-slate-100 hover:shadow-md hover:z-10'
                            : 'bg-slate-50 border-slate-100 opacity-60'
                        }`}
                    >
                      <div className='flex justify-between items-start mb-6'>
                        <div className='flex gap-4'>
                          <div
                              className={`w-12 h-12 rounded-2xl ${item.color} flex items-center justify-center shadow-inner`}>
                            { item.isSpecialIcon ? (
                              <svg className='w-6 h-6' fill='none' viewBox='0 0 32 32' stroke='currentColor'>
                                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2.5}
                                    d='M11 6h10a2 2 0 012 2v16a2 2 0 01-2 2H11a2 2 0 01-2-2V8a2 2 0 012-2zM11 12h10M11 16h10M11 20h10'/>
                              </svg>
                            ) : (
                              <svg className='w-6 h-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d={item.icon}/>
                              </svg>
                            ) }
                          </div>
                          <div>
                            <h3 className='text-sm font-black text-slate-800 tracking-tight leading-tight'>{ item.label }</h3>
                            <p className='text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5'>Premium
                              Service</p>
                          </div>
                        </div>
                        <ToggleInput
                            toggled={isActive}
                            handleToggle={() => toggleLicense(item.id)}
                            name={item.label}
                        />
                      </div>

                      <div className={`grid grid-cols-2 gap-3 mt-auto ${!isActive ? 'pointer-events-none' : ''}`}>
                        <DatePicker
                            label='Start Date'
                            asSingle={true}
                            useRange={false}
                            startDate={organizationLicenses[LICENSE_KEY_MAP[item.id]]?.startDate ?? null}
                            endDate={organizationLicenses[LICENSE_KEY_MAP[item.id]]?.startDate ?? null}
                            onChange={(date) => updateLicenseDate(item.id, 'startDate', date)}
                        />
                        <DatePicker
                            label='End Date'
                            asSingle={true}
                            useRange={false}
                            startDate={organizationLicenses[LICENSE_KEY_MAP[item.id]]?.endDate ?? null}
                            endDate={organizationLicenses[LICENSE_KEY_MAP[item.id]]?.endDate ?? null}
                            onChange={(date) => updateLicenseDate(item.id, 'endDate', date)}
                        />
                      </div>
                      { item.creditUnit && (
                        <div className={`mt-3 ${!isActive ? 'pointer-events-none' : ''}`}>
                          <TextInput
                              label={`Credits / month (${item.creditUnit})`}
                              name={`${item.id}-monthly-credits`}
                              type='number'
                              min='0'
                              placeholder='Unlimited'
                              value={monthlyCreditsValue(item.id)}
                              onChange={(e) => updateMonthlyCredits(item.id, e.target.value)}
                          />
                          <p className='text-[10px] text-slate-400 font-medium mt-1'>Blank = unlimited · 0 = uploads blocked</p>
                        </div>
                      ) }
                    </div>
                  );
                }) }
              </div>
            </div>
          </div>

          { /* Footer Actions */ }
          <div className='bg-slate-50/80 border-t border-slate-100 px-10 py-8 flex items-center justify-end gap-4'>
            <Button
                label='Cancel'
                variant='secondary'
                className='px-8 py-3 rounded-2xl font-bold border-none bg-transparent hover:bg-slate-100 text-slate-500'
                onClick={() => history.push('/organizations')}
            />
            <div className='relative group/create'>
              <Button
                  label='Create Organization'
                  variant='primary'
                  hasIcon
                  disabled={isCreateDisabled}
                  icon={
                    <svg className='w-5 h-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2.5} d='M12 4v16m8-8H4' />
                    </svg>
                  }
                  className='rounded-2xl px-10 py-4 font-bold text-sm shadow-xl shadow-emerald-200/50 hover:shadow-2xl hover:-translate-y-1 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none disabled:shadow-none disabled:translate-y-0'
                  onClick={handleCreateOrganization}
              />
              { isCreateDisabled && (
                <div className='absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-slate-800 text-white text-[11px] font-medium text-center rounded-xl px-3 py-2 shadow-lg opacity-0 group-hover/create:opacity-100 transition-opacity pointer-events-none z-50'>
                  { disabledTooltip }
                  <div className='absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800' />
                </div>
              ) }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrganization;
